<template>
  <section
    class="quick-navigation"
    id="quick-navigation"
    ref="root"
    :class="{ 'is-stuck': isStuck }"
  >
    <div class="wrapper" ref="inner">
      <nav class="container">
        <ul>
          <li
            v-for="(item, i) in sections"
            :key="i"
            :class="{ active: item.id === currentId }"
          >
            <a
              :href="`#${item.id}`"
              :ref="`${item.id}-link`"
              @click.prevent.stop="scrollToSection(item)"
              >{{
                item.dataset.quicknav
              }}</a
            >
          </li>
          <li v-if="showSharePageLink">
            <button class="btn link" type="button" @click.prevent="initShare()">
              {{ texts.shareText }}
            </button>
          </li>
        </ul>
      </nav>
    </div>
  </section>
</template>

<script>
  import { useStuck } from "Composables/useStuck";
  import { useQuickNav } from "Main/composables/useQuickNav";
  import { initShare } from "Main/share";
  import debounce from "lodash-es/debounce";
  import { ref } from "vue";

  export default {
    beforeUnmount() {
      this.maybeRemoveRoleSelectorListener();
      window.removeEventListener("resize", this.debouncedInitActiveSections);

      if (this.sectionObserver) {
        this.sectionObserver.disconnect();
      }
    },

    computed: {
      activeSectionOffset() {
        const viewportHeight = Math.max(
          document.documentElement.clientHeight,
          window.innerHeight || 0
        );
        return viewportHeight - this.qnHeight - this.headerHeight - 20;
      },

      isProfilePage() {
        return document.querySelector(".single-funeral_counselors");
      },

      roleSelector() {
        return this.isProfilePage?.querySelector("#roleSelect");
      },

      topSectionOffset() {
        return this.sections[0].getBoundingClientRect().top + window.scrollY;
      },
    },

    data() {
      return {
        currentId: null,
        linkClicked: false,
        sectionObserver: null,
        showSharePageLink: !!this.$mountEl.dataset.showSharePage,
        texts: theme_vars.quickNavigation,
        windowWidth: window.innerWidth,
      };
    },

    methods: {
      // The link for the section that just left the viewport keeps its focus
      // ring; every other one drops it, so focus never trails behind the tab
      // the page has moved on to.
      blurLinksOutsideSection(sectionId) {
        for (const section of this.sections) {
          const closestSection = section.closest("section");

          if (closestSection && closestSection.id === sectionId) {
            continue;
          }

          const linkRef = this.$refs[`${section.id}-link`];
          const linkEl = Array.isArray(linkRef) ? linkRef[0] : linkRef;

          if (linkEl) {
            linkEl.blur();
          }
        }
      },
      calculateScrollOffset(section) {
        const sectionOffset =
          section.getBoundingClientRect().top + window.scrollY;
        return Math.abs(sectionOffset - this.qnHeight - this.headerHeight) + 2;
      },

      checkAndMakeSectionActive(entries) {
        if (this.linkClicked) {
          return;
        }

        for (const entry of entries) {
          if (!entry.isIntersecting) {
            this.blurLinksOutsideSection(entry.target.id);
            continue;
          }

          const sectionQuickNavTarget =
            entry.target.querySelector("[data-quicknav]");

          this.currentId = sectionQuickNavTarget
            ? sectionQuickNavTarget.id
            : entry.target.id;
        }
      },

      initActiveSections() {
        if (
          !(this.isObserverSupported && this.sections.length) ||
          this.activeSectionOffset <= 0
        ) {
          return false;
        }

        if (this.sectionObserver) {
          this.sectionObserver.disconnect();
        }

        this.sectionObserver = new IntersectionObserver(
          this.checkAndMakeSectionActive,
          {
            rootMargin: `0px 0px -${this.activeSectionOffset}px`,
          }
        );

        for (const el of this.sections) {
          const section = el.closest("section");
          this.sectionObserver.observe(section);
        }
      },
      initShare,

      maybeAddRoleSelectorListener() {
        if (!this.roleSelector) {
          return;
        }

        this.roleSelector.addEventListener("change", this.updateSections);
      },

      maybeRemoveRoleSelectorListener() {
        if (!this.roleSelector) {
          return;
        }

        this.roleSelector.removeEventListener("change", this.updateSections);
      },

      scrollToSection(section) {
        const offsetTop = this.calculateScrollOffset(section);

        this.linkClicked = true;
        this.currentId = section.id;
        history.pushState(null, null, `#${section.id}`);

        window.scrollTo({
          behavior: "smooth",
          top: offsetTop,
        });

        window.requestAnimationFrame(() => {
          setTimeout(() => {
            this.linkClicked = false;
          }, 1000);
        });
      },

      updateSections() {
        // Wait for the DOM to update
        setTimeout(() => {
          this.getSections();

          this.$nextTick(() => {
            this.initActiveSections();
          });
        }, 200);
      },
    },

    mounted() {
      this.initActiveSections();
      this.maybeAddRoleSelectorListener();
      this.watchStuck(this.root);
      this.debouncedInitActiveSections = debounce(this.initActiveSections, 150);
      window.addEventListener("resize", this.debouncedInitActiveSections);
    },
    name: "QuickNavigation",

    setup() {
      const root = ref(null);
      const {
        qnInitialOffset,
        qnHeight,
        headerHeight,
        sections,
        qnLinks,
        isObserverSupported,
        getSections,
        isElementVisible,
        initOffsets,
      } = useQuickNav(root);
      const { isStuck, watchStuck } = useStuck();
      return {
        getSections,
        headerHeight,
        initOffsets,
        isElementVisible,
        isObserverSupported,
        isStuck,
        qnHeight,
        qnInitialOffset,
        qnLinks,
        root,
        sections,
        watchStuck,
      };
    },
  };
</script>
